import React from 'react';
import { useResizeDetector } from 'react-resize-detector';
import styled from 'styled-components';

import Column from 'components/common/Column';
import Text from 'components/common/Text';
import Divider from 'components/Divider';
import ListSearchContainer from './components/ListSearchContainer';
import RequestBottomSheet from './components/RequestBottomSheet';
import SettlementCard from './components/SettlementCard';
import SettlementListHeader from './components/SettlementListHeader';
import SettlementListRequestModal from './components/SettlementListRequestModal';
import { useSettlementListHook } from './hooks/useSettlementListHook';
import SettlementFailedContainer from 'containers/SettlementRequest/components/SettlementFailedContainer';
import { isEmpty } from 'lodash';
import EmptySettlement from 'containers/Settlement/components/EmptySettlement';
import Loading from 'components/Loading/Loading';
import WrapperContainer from 'components/common/Wrapper';
import { MAX_WIDTH } from 'App';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  width: 100%;
  max-width: 768px;
`;

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${props => (props.$isMobile ? '0 20px 192px' : '0 40px 192px')};
  margin: 0 auto;

  width: 100%;
  min-height: calc(100dvh - 146px);

  background-color: #fff;
`;

const SettlementListPage = () => {
  const {
    settlementList,
    selectedSettlement,
    termType,
    isOpenSearchModal,
    isOpenRequestModal,
    isShowRequireInvoice,
    setIsOpenRequestModal,
    setIsOpenSearchModal,
    handleSetTermType,
    handleResetTermType,
    handleAcceptFilter,
    handleSelectSettlement,
    handleRequestInvoices,
    refetchSettlementList,
    handleShowRequireInvoice,
  } = useSettlementListHook();

  const { ref, width } = useResizeDetector();

  const isMobile = width ? width <= MAX_WIDTH : window.innerWidth <= MAX_WIDTH;

  if (!settlementList) {
    return <Loading />;
  }

  const isHaveMinusSettlement = settlementList.list?.some(item =>
    item.edges.some(
      edge =>
        Number(edge.node.totalPayment.value) < 0 && edge.node.invoice === null,
    ),
  );

  const minusSettlements = settlementList.list?.flatMap(item =>
    item.edges
      .filter(
        edge =>
          Number(edge.node.totalPayment.value) < 0 &&
          edge.node.invoice === null,
      )
      .map(edge => edge.node.id),
  );

  const isAllMinusSettlementsSelected = minusSettlements?.every(
    minusSettlementId =>
      selectedSettlement.some(selected => selected.id === minusSettlementId),
  );

  return (
    <WrapperContainer isMobile={isMobile}>
      <Wrapper ref={ref}>
        <Container $isMobile={isMobile}>
          <SettlementListHeader
            isMobile={isMobile}
            isShowRequireInvoice={isShowRequireInvoice}
            handleIsOpenSearchModal={() => setIsOpenSearchModal(true)}
            handleShowRequireInvoice={handleShowRequireInvoice}
          />
          {isHaveMinusSettlement && (
            <Column style={{ paddingBottom: '24px' }}>
              <SettlementFailedContainer
                isMobile={isMobile}
                type="isListPage"
              />
            </Column>
          )}
          <Column gap="32px">
            {isEmpty(settlementList.list) ? (
              <EmptySettlement textContent="조회되는 내역이 없어요" />
            ) : (
              settlementList.list.map((listItem, listIdx) => {
                return (
                  <React.Fragment key={listIdx}>
                    <Column gap="24px" align="flex-start">
                      <Text color="#46474c" size="20px" weight={600}>
                        {listItem.title}
                      </Text>
                      <Column gap="16px">
                        {listItem.edges.map((settlement, settlementIdx) => {
                          return (
                            <SettlementCard
                              isMobile={isMobile}
                              settlement={settlement.node}
                              selectedSettlement={selectedSettlement}
                              isHaveMinusSettlement={isHaveMinusSettlement}
                              handleSelectSettlement={handleSelectSettlement}
                              refetchSettlementList={refetchSettlementList}
                              key={settlementIdx}
                            />
                          );
                        })}
                      </Column>
                    </Column>
                    {settlementList.list.length !== listIdx + 1 && <Divider />}
                  </React.Fragment>
                );
              })
            )}
          </Column>
          <RequestBottomSheet
            selectSettlement={selectedSettlement}
            isAllMinusSettlementsSelected={isAllMinusSettlementsSelected}
            handleIsOpenRequestModal={() => setIsOpenRequestModal(true)}
          />
          {isOpenSearchModal && (
            <ListSearchContainer
              isMobile={isMobile}
              termType={termType}
              handleSetTermType={handleSetTermType}
              handleResetTermType={handleResetTermType}
              handleAcceptFilter={handleAcceptFilter}
              onClose={() => setIsOpenSearchModal(false)}
            />
          )}
          {isOpenRequestModal && (
            <SettlementListRequestModal
              isMobile={isMobile}
              selectedSettlement={selectedSettlement}
              onClose={() => {
                setIsOpenRequestModal(false);
              }}
              handleRequestInvoices={handleRequestInvoices}
              refetchSettlementList={refetchSettlementList}
            />
          )}
        </Container>
      </Wrapper>
    </WrapperContainer>
  );
};

export default SettlementListPage;
